/**
 * ReportCardTemplate — editable US Letter (8.5" x 11") report card template.
 * True print size: 2550 x 3300 px @ 300 DPI. On screen at 96 DPI = 816 x 1056
 * CSS px. All colors + fonts are extracted from the reference JPEG:
 *   - Bebas Neue (headers/labels)  - Kalam (handwriting)  - Lato (body)
 */
import { GOLD_SEAL_URL } from "./reportTemplateAssets";

// -------- tokens (from reference image analysis) --------
const CREAM = "#f4e9d5";
const NAVY = "#1a324b";
const NAVY_SOFT = "#405362";
const RED = "#c63c3c";
const RED_DARK = "#8e2a2a";
const RED_HEART = "#b33f3f";
const SUBJECT_TXT = "#3a4d5f";

// -------- default sample data (matches reference) --------
export const DEFAULT_REPORT = {
  petName: "CHAI",
  photoUrl:
    "https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&w=1200&q=80",
  subjects: [
    { icon: "heart", label: "Cuteness", grade: "A+" },
    { icon: "paw", label: "Potty Training", grade: "A+" },
    { icon: "ear", label: "Listening", grade: "A" },
    { icon: "moon", label: "Healing with on walks", grade: "C-" },
    { icon: "bone", label: "Napping", grade: "A++" },
    { icon: "ball", label: "Fetch", grade: "A" },
    { icon: "house", label: "Guarding the House", grade: "B+" },
    { icon: "heart-outline", label: "Being sweet", grade: "A+++" },
  ],
  notes: [
    "Her new favorite toys are dog frisbee, Jack's cat toy & empty plastic bottle.",
    "She LOVES water (to play in, not drink).",
    "Jack & Gunner are her new besties.",
    "Good good girl - good listener.",
  ],
  stampText: "WAY TO GO, CHAI!",
  bottomText: "KEEP UP THE PAW-SOME WORK!",
  editionNumber: "00247",
};

// -------- icons (red, filled, matching reference) --------
const IconHeart = ({ color = RED_HEART, filled = true, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 21s-7.5-4.5-9.5-9.2C1 8.4 3 5 6.4 5c2 0 3.4 1.1 4.1 2.3.7-1.2 2.1-2.3 4.1-2.3 3.4 0 5.4 3.4 3.9 6.8C19.5 16.5 12 21 12 21z"
      fill={filled ? color : "none"}
      stroke={color}
      strokeWidth="2"
    />
  </svg>
);

const IconPaw = ({ color = NAVY, size = 24 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
    <g fill={color}>
      <ellipse cx="18" cy="20" rx="6" ry="8" />
      <ellipse cx="32" cy="14" rx="6" ry="8" />
      <ellipse cx="46" cy="20" rx="6" ry="8" />
      <ellipse cx="10" cy="36" rx="5" ry="7" />
      <ellipse cx="54" cy="36" rx="5" ry="7" />
      <path d="M32 28c-8 0-14 8-14 15 0 6 5 9 14 9s14-3 14-9c0-7-6-15-14-15z" />
    </g>
  </svg>
);

const IconEar = ({ color = RED, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 20c-2 0-3-2-3-4V9a6 6 0 1 1 12 0c0 3-2 4-3 5s-1 2-1 3-1 3-3 3z" fill={color} />
    <path d="M18 6l3 1M18 10l3 1M18 14l3 1" />
  </svg>
);

const IconMoon = ({ color = RED, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path d="M20 15a8 8 0 1 1-8-11 6 6 0 0 0 8 11z" fill={color} />
    <path d="M5 4l0.6 1.4L7 6l-1.4 0.6L5 8l-0.6-1.4L3 6l1.4-0.6z" fill={color} />
    <path d="M18 3l0.5 1.2L20 4.7l-1.5 0.5L18 6.5l-0.5-1.3L16 4.7l1.5-0.5z" fill={color} />
  </svg>
);

const IconBone = ({ color = RED, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden fill={color}>
    <path d="M5 9a2.5 2.5 0 1 1 2.5-4A2.5 2.5 0 0 1 11 6l6 6a2.5 2.5 0 0 1 4 2.5A2.5 2.5 0 0 1 18.5 17 2.5 2.5 0 0 1 15 20l-6-6a2.5 2.5 0 0 1-4-2.5A2.5 2.5 0 0 1 5 9z" />
  </svg>
);

const IconBall = ({ color = RED, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <circle cx="12" cy="12" r="9" fill={color} />
    <path d="M4 8c3 2 13 2 16 0M4 16c3-2 13-2 16 0" stroke={CREAM} strokeWidth="1.6" fill="none" />
  </svg>
);

const IconHouse = ({ color = RED, size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path d="M4 12l8-7 8 7v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill={color} />
    <circle cx="12" cy="15" r="2" fill={CREAM} />
  </svg>
);

// Hand-drawn double-outline star (navy)
const IconStar = ({ size = 60 }) => (
  <svg viewBox="0 0 80 80" width={size} height={size} aria-hidden fill="none" stroke={NAVY} strokeLinejoin="round" strokeLinecap="round">
    <path d="M40 9l9 19 21 3-15 15 3 21-18-10-18 10 3-21L10 31l21-3z" strokeWidth="3" />
    <path d="M40 14l7 15 17 2-12 12 3 17-15-8-15 8 3-17L21 31l17-2z" strokeWidth="1.5" />
    <path d="M64 12l3 4M12 14l4 3M68 22l3 1M8 22l4 1" strokeWidth="2" />
  </svg>
);

const ICONS = {
  heart: IconHeart,
  "heart-outline": (p) => <IconHeart {...p} filled={false} />,
  paw: (p) => <IconPaw {...p} color={RED} />,
  ear: IconEar,
  moon: IconMoon,
  bone: IconBone,
  ball: IconBall,
  house: IconHouse,
};

// -------- Fishtail V-cut ribbon --------
const Ribbon = ({ text }) => (
  <svg viewBox="0 0 720 170" width="100%" style={{ display: "block", overflow: "visible" }} aria-hidden>
    {/* darker shadow tails (fishtail V-cut on outer edge) */}
    <polygon points="8,32 40,85 8,138 88,120 88,50" fill={RED_DARK} />
    <polygon points="712,32 680,85 712,138 632,120 632,50" fill={RED_DARK} />
    {/* main red body (banner) */}
    <polygon points="70,18 650,18 650,152 70,152" fill={RED} />
    {/* small red_dark under-shadow on body ends for depth */}
    <polygon points="70,152 88,152 88,18 70,18" fill={RED_DARK} opacity="0.35" />
    <polygon points="632,18 650,18 650,152 632,152" fill={RED_DARK} opacity="0.35" />
    {/* inner cream dashed border */}
    <rect x="86" y="34" width="548" height="102" fill="none" stroke={CREAM} strokeWidth="2" strokeDasharray="7 4" />
    {/* small paws */}
    <g transform="translate(120,66) scale(0.55)"><IconPaw color={CREAM} /></g>
    <g transform="translate(566,66) scale(0.55)"><IconPaw color={CREAM} /></g>
    {/* text */}
    <text
      x="360"
      y="118"
      textAnchor="middle"
      fontFamily="'Bebas Neue', 'Oswald', sans-serif"
      fontSize="88"
      fontWeight="400"
      fill={CREAM}
      letterSpacing="6"
    >
      {text}
    </text>
  </svg>
);

// -------- NOTES header ribbon (navy with red rays) --------
const NotesRibbon = () => (
  <svg viewBox="0 0 300 62" width="100%" style={{ display: "block", overflow: "visible" }} aria-hidden>
    <polygon points="4,31 24,10 24,52 4,45" fill={NAVY} />
    <polygon points="296,31 276,10 276,52 296,45" fill={NAVY} />
    <rect x="20" y="8" width="260" height="46" fill={NAVY} />
    {/* red rays */}
    <g stroke={RED} strokeWidth="2.4" strokeLinecap="round">
      <path d="M30 31l-12 -6" />
      <path d="M30 31l-12 0" />
      <path d="M30 31l-12 6" />
      <path d="M270 31l12 -6" />
      <path d="M270 31l12 0" />
      <path d="M270 31l12 6" />
    </g>
    <text x="150" y="42" textAnchor="middle" fontFamily="'Bebas Neue', 'Oswald', sans-serif" fontSize="30" fill={CREAM} letterSpacing="8">
      NOTES
    </text>
  </svg>
);

// -------- Subject row --------
const SubjectRow = ({ icon, label, grade, first }) => {
  const Icon = ICONS[icon] || IconHeart;
  return (
    <div className={`grid grid-cols-[70px_1fr_120px] items-center ${first ? "" : "border-t"}`} style={{ borderColor: NAVY }}>
      <div className="flex items-center justify-center py-2.5">
        <Icon size={30} />
      </div>
      <div className="border-l py-2.5 pl-5" style={{ borderColor: NAVY, fontFamily: "'Kalam', cursive", fontSize: "24px", lineHeight: 1.1, color: SUBJECT_TXT }}>
        {label}
      </div>
      <div className="border-l py-1 text-center" style={{ borderColor: NAVY, fontFamily: "'Kalam', cursive", fontWeight: 700, fontSize: "38px", lineHeight: 1, color: RED_HEART }}>
        {grade}
      </div>
    </div>
  );
};

// -------- Note row --------
const NoteRow = ({ text, last }) => (
  <div className={`flex items-start gap-2.5 py-2 ${last ? "" : "border-b border-dotted"}`} style={{ borderColor: last ? "transparent" : "#c9b98a" }}>
    <div className="mt-0.5 shrink-0">
      <IconPaw color={NAVY} size={20} />
    </div>
    <p style={{ fontFamily: "'Kalam', cursive", fontSize: "18px", lineHeight: 1.25, color: NAVY }}>
      {text}
    </p>
  </div>
);

// -------- Rubber-stamp "WAY TO GO" (red text, red dashed border) --------
const WayToGoStamp = ({ text }) => (
  <div
    className="relative inline-block rounded-[3px] px-6 py-3"
    style={{
      border: `3px dashed ${RED}`,
      transform: "rotate(-2deg)",
      background: "transparent",
    }}
  >
    <div style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", fontSize: "36px", lineHeight: 1, letterSpacing: "3px", color: RED }}>
      {text} <IconHeart size={22} color={RED_HEART} />
    </div>
  </div>
);

// -------- Main template --------
export default function ReportCardTemplate({ data = DEFAULT_REPORT }) {
  const d = { ...DEFAULT_REPORT, ...data };
  return (
    <div
      className="report-page relative overflow-hidden"
      style={{
        width: "8.5in",
        height: "11in",
        background: CREAM,
        boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
      }}
      data-testid="report-card-template"
    >
      {/* solid navy-grey border, hand-drawn feel */}
      <div
        className="absolute"
        style={{
          inset: "0.32in",
          border: `4px solid ${NAVY_SOFT}`,
          borderRadius: "8px",
          pointerEvents: "none",
        }}
        aria-hidden
      />

      {/* top-left decorative paw */}
      <div className="absolute" style={{ left: "0.55in", top: "0.55in" }} aria-hidden>
        <IconPaw color={NAVY} size={42} />
      </div>

      <div className="absolute" style={{ inset: "0.9in 0.9in 0.7in 0.9in" }}>
        {/* HEADER: title/ribbon (LEFT) + photo (RIGHT) */}
        <div className="grid grid-cols-[1.15fr_1fr] items-start gap-8">
          <div className="pt-2">
            <h1
              style={{
                fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                fontSize: "76px",
                lineHeight: 0.95,
                letterSpacing: "3px",
                color: NAVY,
              }}
            >
              REPORT&nbsp;CARD
            </h1>
            <div
              className="mt-2 flex items-center gap-3"
              style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", fontSize: "32px", color: NAVY, letterSpacing: "2px" }}
            >
              <IconHeart size={22} /> for <IconHeart size={22} />
            </div>
            <div className="mt-3">
              <Ribbon text={d.petName} />
            </div>
          </div>

          {/* Photo — tilted ~4° CW with washi tape */}
          <div className="relative flex justify-center pt-2">
            <div className="relative" style={{ transform: "rotate(4deg)" }}>
              <div
                className="absolute z-10 rounded-sm"
                style={{
                  width: "110px",
                  height: "34px",
                  top: "-10px",
                  right: "-8px",
                  transform: "rotate(14deg)",
                  background:
                    "repeating-linear-gradient(60deg,#d9c4a2,#d9c4a2 8px,#c9b48c 8px,#c9b48c 12px)",
                  opacity: 0.9,
                  boxShadow: "0 2px 3px rgba(0,0,0,0.15)",
                }}
              />
              <div
                className="bg-white p-1.5 shadow-[0_10px_18px_-6px_rgba(0,0,0,0.35)]"
                style={{ width: "2.8in", height: "3.15in" }}
              >
                <div className="h-full w-full overflow-hidden" style={{ background: NAVY }}>
                  <img
                    src={d.photoUrl}
                    alt={d.petName}
                    className="h-full w-full object-cover"
                    data-testid="report-photo"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MIDDLE: subjects (LEFT) + notes (RIGHT) */}
        <div className="mt-8 grid grid-cols-[1.15fr_1fr] gap-5">
          <div className="rounded-md" style={{ border: `3px solid ${NAVY}` }} data-testid="subjects-table">
            <div
              className="grid grid-cols-[70px_1fr_120px] items-center py-2.5"
              style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", fontSize: "22px", letterSpacing: "3px", color: NAVY }}
            >
              <div />
              <div className="border-l pl-5" style={{ borderColor: NAVY }}>SUBJECT</div>
              <div className="border-l text-center" style={{ borderColor: NAVY }}>GRADE</div>
            </div>
            {d.subjects.map((s, i) => (
              <SubjectRow key={i} icon={s.icon} label={s.label} grade={s.grade} first={false} />
            ))}
          </div>

          <div className="rounded-md p-3" style={{ border: `3px solid ${NAVY}` }} data-testid="notes-panel">
            <div className="mx-auto mb-3 w-[86%]">
              <NotesRibbon />
            </div>
            <div className="px-1">
              {d.notes.map((n, i) => (
                <NoteRow key={i} text={n} last={i === d.notes.length - 1} />
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER: LIMITED EDITION | WAY TO GO | Star + Seal */}
        <div className="mt-6 grid grid-cols-[1fr_1.1fr_1fr] items-center gap-3">
          <div className="flex flex-col items-start gap-1">
            <IconPaw color={NAVY} size={56} />
            <div style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", fontSize: "18px", letterSpacing: "2px", color: NAVY }}>
              LIMITED EDITION
            </div>
            <div style={{ fontFamily: "'Kalam', cursive", fontSize: "22px", color: NAVY }}>
              No. {d.editionNumber}
            </div>
            <svg width="130" height="8" viewBox="0 0 130 8" aria-hidden>
              <path d="M2 4 C 22 0, 44 8, 66 4 S 108 0, 128 4" stroke={NAVY} strokeWidth="1.5" fill="none" />
            </svg>
          </div>

          <div className="flex justify-center">
            <WayToGoStamp text={d.stampText} />
          </div>

          <div className="flex items-center justify-end gap-2">
            <IconStar size={60} />
            <img
              src={GOLD_SEAL_URL}
              alt="Official Paw-Some Report seal"
              style={{ width: "134px", height: "134px", transform: "rotate(-12deg)", mixBlendMode: "multiply" }}
              data-testid="report-gold-seal"
            />
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
              fontSize: "22px",
              letterSpacing: "2.5px",
              color: NAVY,
              borderBottom: `2px solid ${NAVY}`,
              paddingBottom: "2px",
            }}
          >
            {d.bottomText}
          </div>
          <IconHeart size={18} />
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: 8.5in 11in; margin: 0; }
          .report-page { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
